import React, { useMemo } from 'react';
import { format } from 'date-fns';
import {
  Line, LineChart, ReferenceArea, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { buildZoneBands } from './zoneBands';

const METRIC = 'verbs_per_nouns_coef';

const ActionObjectificationChart = ({ allData, data, onPointClick }) => {
  const stats = useMemo(() => {
    const values = allData.map((row) => row[METRIC]).filter(Number.isFinite);
    if (!values.length) return null;
    const mean = values.reduce((sum, value) => sum + value, 0) / values.length;
    const variance = values.length > 1
      ? values.reduce((sum, value) => sum + (value - mean) ** 2, 0) / (values.length - 1)
      : 0;
    const sigma = Math.sqrt(variance);
    const visibleValues = data.map((row) => row[METRIC]).filter(Number.isFinite);
    const min = Math.min(...(visibleValues.length ? visibleValues : values));
    const max = Math.max(...(visibleValues.length ? visibleValues : values));
    const padding = Math.max((max - min) * 0.08, sigma * 0.2, 0.01);
    const lower = min - padding;
    const upper = max + padding;
    return {
      mean, sigma,
      domain: [lower, upper],
    };
  }, [allData, data]);

  if (!stats) {
    return (
      <section style={{ marginTop: 32 }}>
        <h3>Коэффициент опредмеченности действия (глаголы / существительные)</h3>
        <div className="text-muted">Нет данных для отображения</div>
      </section>
    );
  }

  const { mean, sigma, domain } = stats;
  const bands = buildZoneBands(domain, mean, sigma);
  const boundaries = sigma > 0
    ? [-3, -2, -1, 1, 2, 3].map((multiplier) => mean + multiplier * sigma)
      .filter((value) => value > domain[0] && value < domain[1])
    : [];

  return (
    <section style={{ marginTop: 32 }}>
      <h3>Коэффициент опредмеченности действия (глаголы / существительные)</h3>
      <p className="text-muted small">
        Среднее значение коэффициента по всем выбранным сообществам за каждый день.
        Зоны показывают отклонение дневного значения от среднего за выбранный период.
        Красная зона начинается за пределами трёх стандартных отклонений.
        Нажмите на точку, чтобы увидеть исходные сообщения за день.
      </p>
      {data.some((row) => Number.isFinite(row[METRIC])) ? (
        <ResponsiveContainer width="100%" height={620}>
          <LineChart data={data} margin={{ top: 20, right: 30, left: 90, bottom: 50 }}>
            <XAxis
              dataKey="dateTimestamp"
              type="category"
              tickFormatter={(timestamp) => format(new Date(timestamp), 'dd.MM.yyyy')}
              tick={{ fontSize: 10, angle: -45, textAnchor: 'end', dy: 12 }}
              interval="preserveStartEnd"
              minTickGap={50}
              label={{ value: 'Дата', position: 'insideBottom', offset: -45, style: { fontWeight: 600 } }}
            />
            <YAxis
              domain={domain}
              tickCount={8}
              width={60}
              tickFormatter={(value) => Number(value).toFixed(2)}
              tick={{ fontSize: 10 }}
              label={{ value: 'Глаголы / существительные', angle: -90, position: 'left', offset: 20, fontSize: 14 }}
            />
            <Tooltip content={({ active, payload, label }) => {
              if (!active || !payload?.length) return null;
              const metricPoint = payload.find((item) => item.dataKey === METRIC);
              const value = metricPoint?.value;
              if (!Number.isFinite(value)) return null;
              const events = metricPoint?.payload?.timelineEvents || [];
              const deviation = sigma > 0 ? Math.abs((value - mean) / sigma) : 0;
              const zone = deviation > 3 ? 'сильный выход за норму'
                : deviation > 2 ? 'выход за норму'
                  : deviation > 1 ? 'малый выход за норму' : 'норма';
              return (
                <div className="timeline-chart-tooltip">
                  <strong>{format(new Date(label), 'dd.MM.yyyy')}</strong>
                  <div>Глаголы / существительные: {value.toFixed(2)} ({zone})</div>
                  {events.length > 0 && (
                    <div className="timeline-chart-tooltip__events">
                      {events.slice(0, 6).map((event) => (
                        <div key={event.id}>
                          <b>{event.title}</b>
                          {event.owner_username ? ` · ${event.owner_username}` : ''}
                          {event.description ? <small>{event.description}</small> : null}
                        </div>
                      ))}
                      {events.length > 6 ? <div>Ещё событий: {events.length - 6}</div> : null}
                    </div>
                  )}
                </div>
              );
            }} />
            {bands.map((band, index) => (
              <ReferenceArea
                key={index}
                y1={band.from}
                y2={band.to}
                fill={band.color}
                fillOpacity={band.color === '#E57373' ? 0.3 : 0.6}
              />
            ))}
            {boundaries.map((value) => <ReferenceLine key={value} y={value} stroke="#000" strokeWidth={1} />)}
            {data.filter((point) => point.timelineEvents?.length).map((point) => (
              <ReferenceLine
                key={point.dateTimestamp}
                x={point.dateTimestamp}
                stroke={point.timelineEvents.some((event) => event.is_owner) ? '#1976d2' : '#ef6c00'}
                strokeOpacity={0.62}
              />
            ))}
            <Line
              type="monotone"
              dataKey={METRIC}
              stroke="#000"
              strokeWidth={1}
              dot={({ cx, cy, payload }) => (
                Number.isFinite(cx) && Number.isFinite(cy) ? (
                  <circle
                    cx={cx}
                    cy={cy}
                    r={payload.timelineEvents?.length ? 7 : 2.5}
                    fill={sigma > 0 && Math.abs(payload[METRIC] - mean) > 3 * sigma ? '#C62828' : '#000'}
                    stroke="#fff"
                    strokeWidth={1}
                  />
                ) : null
              )}
              activeDot={{
                r: 6,
                fill: '#000',
                stroke: '#fff',
                strokeWidth: 2,
                cursor: 'pointer',
                onMouseDown: (_event, point, index) => onPointClick(point.payload, index),
              }}
            />
          </LineChart>
        </ResponsiveContainer>
      ) : (
        <div className="text-muted">Нет данных в выбранном окне</div>
      )}
    </section>
  );
};

export default ActionObjectificationChart;
